import Head from 'next/head'
import Link from 'next/link'

import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { Eye, EyeOff, KeyRound, Lock } from 'lucide-react'
import { adminFetch } from '@/lib/admin/client'
import { useSettings } from '@/components/providers/settings-provider'
import { BrandMark } from '@/components/generals/logo'
import { Button, Field, Input } from '@/components/admin/admin-ui'

interface AdminLoginProps {
    // True while no admin account exists yet: the page then offers to create the first one.
    needsSetup: boolean
}

const PasswordInput: FC<{ id: string, value: string, onChange: (value: string) => void, autoComplete: string, placeholder?: string }> = ({ id, value, onChange, autoComplete, placeholder }): ReactNode => {
    const [visible, setVisible] = useState(false)

    return (
        <div className='relative'>
            <Input
                id={id}
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={event => onChange(event.target.value)}
                autoComplete={autoComplete}
                placeholder={placeholder}
                required
                className='pr-12'
            />
            <button
                type='button'
                onClick={() => setVisible(current => !current)}
                aria-label={visible ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                className='absolute right-2 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full text-cocoa-500 hover:bg-cream-100'
            >
                {visible ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
            </button>
        </div>
    )
}

export const AdminLogin: FC<AdminLoginProps> = ({ needsSetup }): ReactNode => {
    const settings = useSettings()
    const [mode, setMode] = useState<'login' | 'setup'>(needsSetup ? 'setup' : 'login')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [name, setName] = useState('')
    const [setupCode, setSetupCode] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setError(null)

        if (mode === 'setup' && password !== confirm) {
            setError('Konfirmasi kata sandi belum sama.')

            return
        }

        setBusy(true)

        try {
            if (mode === 'login') {
                await adminFetch('/api/admin/auth/login', { method: 'POST', body: { email, password } })
            }
            else {
                await adminFetch('/api/admin/auth/setup', { method: 'POST', body: { setupCode, name, email, password } })
            }

            window.location.assign('/admin')
        }
        catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Terjadi kesalahan. Coba lagi.')
            setBusy(false)
        }
    }

    return (
        <main className='flex min-h-screen items-center justify-center bg-[radial-gradient(90%_70%_at_50%_20%,#fffaf3_0%,#f9ece2_60%,#f2ddd0_100%)] px-4 py-12'>
            <Head>
                <title>{`${mode === 'login' ? 'Masuk Admin' : 'Buat Akun Admin'} - ${settings.storeName}`}</title>
                <meta name='robots' content='noindex, nofollow' />
            </Head>

            <div className='w-full max-w-md'>
                <div className='flex flex-col items-center text-center'>
                    <BrandMark size={150} priority className='ring-4 ring-white shadow-[0_24px_50px_-26px_rgba(58,40,30,0.6)]' />
                    <h1 className='mt-6 font-heading text-3xl text-cocoa-900 font-soft'>
                        {mode === 'login' ? 'Masuk ke Dashboard' : 'Buat akun admin pertama'}
                    </h1>
                    <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                        {mode === 'login'
                            ? 'Kelola produk, kategori, pesanan, dan pengaturan toko.'
                            : 'Akun ini dipakai untuk mengelola toko. Kode pengaturan bisa disalin dari kunci ADMIN_SETUP_CODE di tab Secrets.'}
                    </p>
                </div>

                <form onSubmit={submit} className='mt-8 space-y-5 rounded-[32px] border border-white/70 bg-cream-50/90 p-6 shadow-[0_30px_80px_-40px_rgba(58,40,30,0.6)] backdrop-blur sm:p-8'>
                    {mode === 'setup' && (
                        <>
                            <Field label='Kode pengaturan' htmlFor='setup-code' hint='Hanya dibutuhkan sekali, saat membuat akun pertama.'>
                                <Input id='setup-code' value={setupCode} onChange={event => setSetupCode(event.target.value)} autoComplete='off' required />
                            </Field>
                            <Field label='Nama admin' htmlFor='admin-name'>
                                <Input id='admin-name' value={name} onChange={event => setName(event.target.value)} autoComplete='name' required maxLength={80} />
                            </Field>
                        </>
                    )}

                    <Field label='Email' htmlFor='admin-email'>
                        <Input id='admin-email' type='email' value={email} onChange={event => setEmail(event.target.value)} autoComplete='username' required inputMode='email' />
                    </Field>

                    <Field label='Kata sandi' htmlFor='admin-password' hint={mode === 'setup' ? 'Minimal 8 karakter, berisi huruf dan angka.' : undefined}>
                        <PasswordInput id='admin-password' value={password} onChange={setPassword} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
                    </Field>

                    {mode === 'setup' && (
                        <Field label='Ulangi kata sandi' htmlFor='admin-confirm'>
                            <PasswordInput id='admin-confirm' value={confirm} onChange={setConfirm} autoComplete='new-password' />
                        </Field>
                    )}

                    {error && (
                        <p role='alert' className='rounded-2xl bg-[#f8e0dc] px-4 py-3 text-sm font-semibold text-[#9c3b34]'>
                            {error}
                        </p>
                    )}

                    <Button type='submit' size='lg' loading={busy} className='w-full'>
                        {mode === 'login' ? <Lock className='size-4' /> : <KeyRound className='size-4' />}
                        {mode === 'login' ? 'Masuk' : 'Buat akun dan masuk'}
                    </Button>
                </form>

                <div className='mt-6 flex flex-col items-center gap-2 text-sm'>
                    {needsSetup && (
                        <button
                            type='button'
                            onClick={() => {
                                setMode(mode === 'login' ? 'setup' : 'login')
                                setError(null)
                            }}
                            className='font-semibold text-cocoa-700 underline-offset-4 hover:underline'
                        >
                            {mode === 'login' ? 'Belum punya akun admin?' : 'Sudah punya akun? Masuk'}
                        </button>
                    )}
                    <Link href='/' className='text-muted-foreground underline-offset-4 hover:text-cocoa-800 hover:underline'>
                        Kembali ke toko
                    </Link>
                </div>
            </div>
        </main>
    )
}
